import NudosButton from 'components/NudosComponents/NudosButton/NudosButton';
import { useEffect, useMemo, useState } from 'react';
import { getBillingDataByOrganization, listOrganizations } from 'services/bussines';
import { Iorganization } from 'types/bussines';
import { ICountryDetail } from 'types/countries';
import UploadForm from 'views/bulkUpload/components/UploadForm/UploadForm';
import {
  UploadFormOptionDropdown,
  UploadFormOptionDropdownOptions,
  UploadFormOptionNumberOfDays
} from 'views/bulkUpload/components/UploadForm/UploadForm.types';
import { downloadOrdersTemplate } from 'services/bulkUpload/uploadOrders.service';
import useBulkUploadOrdersState from 'state/useBulkUploadOrdersState';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './DownloadOrdersStep0.scss';

interface DownloadOrdersStep0Props {
  selectedCountry: ICountryDetail | null;
  handleSelectedCountry: (value: ICountryDetail) => void;
  nextStep: () => void;
  countries: ICountryDetail[];
}

const DownloadOrdersStep0 = ({
  selectedCountry,
  handleSelectedCountry,
  nextStep,
  countries
}: DownloadOrdersStep0Props) => {
  const {
    selectedOrganization,
    selectedPaymentAgreement,
    selectedDocument,
    setSelectedOrganization,
    setSelectedPaymentAgreement,
    setSelectedDocument
  } = useBulkUploadOrdersState();

  const [organizations, setOrganizations] = useState<Iorganization[]>([]);
  const [documents, setDocuments] = useState<UploadFormOptionDropdownOptions[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const getDropdownOptions: UploadFormOptionDropdown[] = useMemo(
    () => [
      {
        label: 'País',
        type: 'dropdown',
        options:
          countries &&
          countries.map(country => ({
            label: country.name ?? '',
            value: country.code ?? '',
            handleChange: () => handleSelectedCountry(country)
          })),
        defaultOption: {
          label: selectedCountry?.name ?? 'Selecciona el país',
          value: selectedCountry?.code ?? '',
          isFilled: !!selectedCountry?.code
        }
      },
      {
        label: 'Selecciona el cliente para crearle las órdenes',
        type: 'dropdown',
        options: organizations.map(organization => ({
          label: organization.name ?? '',
          value: organization.id.toString() ?? '',
          handleChange: () => {
            setSelectedOrganization(organization);
            setSelectedDocument(undefined);
          }
        })),
        defaultOption: {
          label: selectedOrganization?.name ?? 'Selecciona el cliente',
          value: selectedOrganization?.id.toString() ?? '',
          isFilled: !!selectedOrganization?.id
        }
      },
      {
        label: 'Documento de pago',
        type: 'dropdown',
        options: documents,
        defaultOption: {
          label: selectedDocument ?? 'Selecciona el documento',
          value: selectedDocument ?? '',
          isFilled: !!selectedDocument
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [countries, organizations, selectedCountry, selectedOrganization, selectedDocument, documents]
  );

  const options = useMemo(() => {
    const getNumberOfDaysOptions: UploadFormOptionNumberOfDays[] = [
      {
        label: 'Días de acuerdo de pago (opcional)',
        type: 'number-of-days',
        handleChange: value => setSelectedPaymentAgreement(+value),
        placeholder: '15 días',
        isFilled: !!selectedPaymentAgreement,
        value: selectedPaymentAgreement
      }
    ];

    return [...getDropdownOptions, ...getNumberOfDaysOptions];
  }, [getDropdownOptions, selectedPaymentAgreement]);

  const fetchOptions = async () => {
    const organizations = await listOrganizations();
    setOrganizations(organizations);
  };

  const downloadTemplate = async () => {
    if (!selectedOrganization || !selectedCountry || !selectedDocument) return;
    const template = await downloadOrdersTemplate(
      selectedOrganization?.id,
      selectedCountry?.name!,
      selectedDocument,
      selectedPaymentAgreement
    );
    window.open(template?.url);
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadTemplate();
      nextStep();
    } catch (error) {
      if (error instanceof SyntaxError) {
        displayErrorNotification({
          customJSXMessage: <>Error en los datos, por favor verifica la información ingresada.</>
        });
      }
      console.error(error);
    }
    setIsDownloading(false);
  };

  const fetchOrganizationBillingData = async () => {
    if (!selectedOrganization) return;
    const billingData = await getBillingDataByOrganization(selectedOrganization.id);
    const docs = Object.values(billingData)?.flatMap(
      data =>
        data.billingEntities?.map(doc => ({
          label: doc.document,
          value: doc.document,
          handleChange: () => setSelectedDocument(doc.document)
        })) || []
    );
    setDocuments(docs || []);
  };

  useEffect(() => {
    fetchOrganizationBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization]);

  useEffect(() => {
    fetchOptions();
  }, []);

  return (
    <UploadForm
      uploadInfo={{
        title: 'Selecciona el país donde quiere crear órdenes',
        description: 'Crearemos una plantilla con la información que necesites',
        icon: 'truck'
      }}
      options={options}
      hideFileInput
      hideAlert
      customClassName="downloadUploadOrdersForm"
      showSeparatorInOptions={false}
      customSubmitSection={
        <NudosButton
          buttonText="Descargar plantilla y continuar"
          handleClick={handleDownloadTemplate}
          isButtonDisabled={!selectedCountry || !selectedOrganization || !selectedDocument}
          customWidth="263px"
          customHeight="44px"
          isButtonLoading={isDownloading}
        />
      }
    />
  );
};

export default DownloadOrdersStep0;
